import React, { ChangeEvent, CSSProperties, ReactNode, useImperativeHandle, useRef } from "react";

interface ISelect {
  children: ReactNode;
  className?: string;
  name?: string;
  value?: string | number | readonly string[] | undefined;
  style?: CSSProperties;
  ref?: React.Ref<HTMLSelectElement>;
  onChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
}

export default function Select(props: ISelect): React.JSX.Element {
  const { className, children, ref, value, ...rest } = props;

  const node = useRef<HTMLSelectElement | null>(null);

  /**
   * React refs refer to DOM elements not to custom compoents.
   * To pass a ref from a custom component to the DOM component of a child
   * (aka nested) component, we previously had to use React.forwardRef.
   *
   * However, React.forwardRef is soon to be deprecated,
   * and the React guys says we can now use a 'ref' prop directly just like other props.
   * @see: https://react.dev/reference/react/forwardRef
   * @see: https://react.dev/blog/2024/12/05/react-19#ref-as-a-prop
   *
   * Also, in addition to the ref passed from the parent component,
   * we are also using an inner ref which is also needed by this component.
   * To have both refs (passed and internal) refer to the same DOM element,
   * we use this useImperativeHandle trick.
   *
   * > Call the useImperativeHandle hook on the outer ref
   * > (which is being forwarded to the child component)
   * > and pass a function that returns the current property of the inner ref,
   * > which is the value that will be set to the current property of the outer ref.
   *
   * > Remember to list any dependencies of the function that returns the ref value,
   * > similar to useEffect.
   *
   * @see: https://stackoverflow.com/a/77055616/1743192
   */
  useImperativeHandle(ref, () => node.current!, []);

  return (
    <select
      ref={node}
      value={value}
      className={`form-control ${className ? className : ""}`}
      {...rest}
    >
      {children}
    </select>
  );
}
